import React from 'react';
import { Code, Database, BarChart3, Cloud, Brain, Zap, Eye, Cpu, TrendingUp, Layers } from 'lucide-react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { useLanguage } from '../../contexts/LanguageContext';
import { useScrollReveal } from '../../hooks/useScrollReveal';

const Skills = ({ skills }) => {
  const { t } = useLanguage();
  const { ref: coreRef, isVisible: coreVisible } = useScrollReveal();
  const { ref: libRef, isVisible: libVisible } = useScrollReveal();
  const { ref: strengthsRef, isVisible: strengthsVisible } = useScrollReveal();

  const skillCategories = [
    {
      title: t('skills.programming'),
      icon: Code,
      items: skills.programming || [],
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-500/10',
      borderGradient: 'from-blue-500 to-blue-600'
    },
    {
      title: t('skills.tools'),
      icon: Cloud,
      items: skills.tools || [],
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-500/10',
      borderGradient: 'from-green-500 to-green-600'
    },
    {
      title: t('skills.databases'),
      icon: Database,
      items: skills.databases || [],
      color: 'text-red-600 dark:text-red-400',
      bgColor: 'bg-red-500/10',
      borderGradient: 'from-red-500 to-red-600'
    },
    {
      title: t('skills.visualization'),
      icon: BarChart3,
      items: skills.visualization || [],
      color: 'text-orange-600 dark:text-orange-400',
      bgColor: 'bg-orange-500/10',
      borderGradient: 'from-orange-500 to-orange-600'
    }
  ];

  const libraryCategories = [
    { title: 'Data Science Core', icon: Brain, color: 'text-purple-600 dark:text-purple-400', bgColor: 'bg-purple-500/10' },
    { title: 'Visualization', icon: Eye, color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-500/10' },
    { title: 'Deep Learning', icon: Cpu, color: 'text-indigo-600 dark:text-indigo-400', bgColor: 'bg-indigo-500/10' },
    { title: 'Computer Vision', icon: Eye, color: 'text-cyan-600 dark:text-cyan-400', bgColor: 'bg-cyan-500/10' },
    { title: 'NLP', icon: Brain, color: 'text-emerald-600 dark:text-emerald-400', bgColor: 'bg-emerald-500/10' },
    { title: 'Time Series', icon: TrendingUp, color: 'text-amber-600 dark:text-amber-400', bgColor: 'bg-amber-500/10' },
    { title: 'Big Data', icon: Cloud, color: 'text-slate-600 dark:text-slate-400', bgColor: 'bg-slate-500/10' },
    { title: 'MLOps', icon: Zap, color: 'text-pink-600 dark:text-pink-400', bgColor: 'bg-pink-500/10' },
    { title: 'AutoML & Optimization', icon: Layers, color: 'text-violet-600 dark:text-violet-400', bgColor: 'bg-violet-500/10' },
    { title: 'Advanced Models', icon: TrendingUp, color: 'text-rose-600 dark:text-rose-400', bgColor: 'bg-rose-500/10' }
  ];

  const renderLibrariesSection = () => {
    if (!skills.libraries || typeof skills.libraries !== 'object') return null;

    return (
      <div ref={libRef} className={`mb-12 reveal-up ${libVisible ? 'visible' : ''}`}>
        <h3 className="text-2xl font-semibold text-foreground mb-8 text-center">
          {t('skills.libraries')}
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.entries(skills.libraries).map(([categoryKey, items], idx) => {
            const category = libraryCategories.find(cat =>
              cat.title.toLowerCase().replace(/\s+/g, '') === categoryKey.toLowerCase().replace(/\s+/g, '')
            ) || libraryCategories[0];

            const IconComponent = category.icon;

            return (
              <Card
                key={categoryKey}
                className="p-6 bg-card dark:bg-card border border-border/50 hover:shadow-lg hover:border-blue-200 dark:hover:border-blue-800 hover:-translate-y-1 transition-all duration-300 relative overflow-hidden"
                style={{ animationDelay: `${idx * 80}ms` }}
              >
                <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${category.bgColor}`} />
                <div className="flex items-center mb-4">
                  <div className={`p-2 rounded-lg ${category.bgColor} mr-3`}>
                    <IconComponent className={`w-5 h-5 ${category.color}`} />
                  </div>
                  <h4 className="text-sm font-semibold text-foreground leading-tight">
                    {categoryKey}
                  </h4>
                </div>

                <div className="flex flex-wrap gap-1.5">
                  {items.map((item, itemIndex) => (
                    <Badge
                      key={itemIndex}
                      variant="secondary"
                      className="text-xs bg-blue-500/10 text-blue-700 dark:text-blue-300 border-0 hover:bg-blue-500/20 transition-colors cursor-default"
                    >
                      {item}
                    </Badge>
                  ))}
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <section id="skills" className="py-20 md:py-32 bg-muted dark:bg-muted">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 text-sm font-medium mb-4 tracking-wide uppercase">
            {t('skills.title')}
          </span>
          <h2 className="text-3xl md:text-5xl font-light text-foreground mb-4">
            {t('skills.title')}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-teal-500 mx-auto rounded-full mb-8" />
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('skills.subtitle')}
          </p>
        </div>

        {/* Core Skills */}
        <div ref={coreRef} className={`mb-16 reveal-up ${coreVisible ? 'visible' : ''}`}>
          <h3 className="text-2xl font-semibold text-foreground mb-8 text-center">
            {t('skills.coreTech')}
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {skillCategories.map((category, index) => {
              const IconComponent = category.icon;
              return (
                <Card
                  key={index}
                  className="p-6 bg-card dark:bg-card border border-border/50 hover:shadow-lg hover:border-blue-200 dark:hover:border-blue-800 hover:-translate-y-1 transition-all duration-300 relative overflow-hidden"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${category.borderGradient}`} />
                  <div className="flex items-center mb-4">
                    <div className={`p-2 rounded-lg ${category.bgColor} mr-3`}>
                      <IconComponent className={`w-5 h-5 ${category.color}`} />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground">
                      {category.title}
                    </h3>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {category.items.map((item, itemIndex) => (
                      <Badge
                        key={itemIndex}
                        variant="secondary"
                        className="bg-blue-500/10 text-blue-700 dark:text-blue-300 border-0 hover:bg-blue-500/20 transition-colors cursor-default"
                      >
                        {item}
                      </Badge>
                    ))}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Libraries Section */}
        {renderLibrariesSection()}

        {/* Key strengths */}
        <div ref={strengthsRef} className={`grid md:grid-cols-3 gap-8 text-center reveal-up ${strengthsVisible ? 'visible' : ''}`}>
          {[
            { icon: Brain, title: t('skills.machineLearning'), desc: t('skills.machineLearningDesc'), gradient: 'from-blue-500 to-blue-600' },
            { icon: BarChart3, title: t('skills.dataAnalytics'), desc: t('skills.dataAnalyticsDesc'), gradient: 'from-green-500 to-green-600' },
            { icon: Cloud, title: t('skills.mlops'), desc: t('skills.mlopsDesc'), gradient: 'from-purple-500 to-purple-600' },
          ].map((item, i) => {
            const IconComp = item.icon;
            return (
              <div key={i} className="p-6" style={{ animationDelay: `${i * 100}ms` }}>
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${item.gradient} flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                  <IconComp className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">{item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Skills;
